// src/utils/questionEnginePrefillHelpers.js
import { toPillDate, toPillDateParts } from './toPillData';

// Is the prefill payload effectively "empty"?
export const isEmptyPrefill = (data) => {
  if (!data) return true;
  if (data?.status === 404 || data?.notFound === true) return true;
  if (typeof data === 'object' && Object.keys(data).length === 0) return true;
  return false;
};

// Copy day/night address if one is missing
export const autoFillMissingAddresses = (formValues = {}) => {
  const hasNight = !!formValues.nightAddress;
  const hasDay = !!formValues.dayAddress;

  const updatedFormValues = { ...formValues };

  if (!hasDay && hasNight) {
    updatedFormValues.dayAddress = formValues.nightAddress;
  } else if (!hasNight && hasDay) {
    updatedFormValues.nightAddress = formValues.dayAddress;
  }

  return updatedFormValues;
};

// Every key the ID-prefill flow may populate for a person: the raw API
// response fields (transformPrefillResponse spreads the response into
// formValues) plus the internal keys it maps them onto.
const PREFILL_PERSON_KEYS = [
  'IDNumber',
  'FirstName',
  'LastName',
  'MaritalStatus',
  'CellNumber',
  'ContactNumber',
  'IsCellNumberEmpty',
  'EmailAddress',
  'IsEmailEmpty',
  'FullAddress',
  'IsFullAddressEmpty',
  'Gender',
  'DateOfBirth',
  'DobDay',
  'DobMonth',
  'DobYear',
  'typeoflicense',
  'licenseissuedate',
  'typeoflicensebind',
  'licenseissuedmonth',
  'licenseissuedyear',
  'email',
  'phone',
  'gender',
  'dateOfBirth',
  'maritalStatus',
  'birthday',
  'nightAddress',
  'dayAddress',
  'licenseFirstIssue',
];

// When the ID number changes, only the previous person's data may be wiped -
// answers that aren't person-derived (property addresses, product selections)
// must survive the reset.
export const stripPrefillPersonValues = (formValues = {}) => {
  const result = { ...formValues };
  PREFILL_PERSON_KEYS.forEach((key) => {
    delete result[key];
  });
  return result;
};

// Map API prefill response onto our internal formValues shape
export const transformPrefillResponse = (apiResponse, formValues = {}) => {
  const result = { ...apiResponse };
  const fullAddress = apiResponse?.FullAddress?.trim();
  const firstName = apiResponse?.FirstName?.trim();
  const lastName = apiResponse?.LastName?.trim();

  if (!formValues.fullName && firstName && lastName) {
    result.FirstName = firstName;
    result.LastName = lastName;
  } else {
    const [first, ...rest] = formValues.fullName?.split(' ') || [];
    result.FirstName = first || '';
    result.LastName = rest.join(' ');
  }

  if (!formValues.nightAddress && fullAddress) {
    result.nightAddress = fullAddress;
    result.dayAddress = fullAddress;
  }

  if (apiResponse?.licenseissuedate) {
    result.licenseFirstIssue = toPillDate(apiResponse.licenseissuedate);
  } else if (
    apiResponse?.licenseissuedyear &&
    apiResponse?.licenseissuedmonth
  ) {
    result.licenseFirstIssue = toPillDateParts(
      apiResponse.licenseissuedyear,
      apiResponse.licenseissuedmonth,
      apiResponse?.licenseissuedday
    );
  }

  result.idNumber = formValues.idNumber;

  result.email = apiResponse?.EmailAddress;
  result.phone = apiResponse?.CellNumber;
  result.gender = apiResponse?.Gender;
  result.dateOfBirth = apiResponse?.DateOfBirth;
  result.maritalStatus = apiResponse?.MaritalStatus;

  if (apiResponse?.DobYear && apiResponse?.DobMonth && apiResponse?.DobDay) {
    result.birthday = {
      year: String(apiResponse.DobYear),
      month: String(apiResponse.DobMonth).padStart(2, '0'),
      day: String(apiResponse.DobDay).padStart(2, '0'),
    };
  }

  return result;
};

export const mergePrefillIntoFormValues = (apiResponse, formValues = {}) => {
  if (isEmptyPrefill(apiResponse)) {
    return autoFillMissingAddresses(formValues);
  }

  const transformed = transformPrefillResponse(apiResponse, formValues);
  return autoFillMissingAddresses({
    ...formValues,
    ...transformed,
  });
};
